import { useCallback, useEffect, useMemo, useReducer } from "react";
import { toast } from "react-toastify";
import {
  getNextStageId,
  PIPELINE_STAGES,
} from "../constants/pipelineStages";
import { pipelineService } from "../../../services/pipelineService";

const DT_CARD = "application/x-pipeline-card-id";

/** @typedef {Object} PipelineCardModel
 * @property {string} id
 * @property {string} campaignId
 * @property {string} stageId
 * @property {string} name
 * @property {string} handle
 * @property {string[]} niches
 * @property {string[]} platforms
 * @property {number} followers
 * @property {number} engagementRate
 * @property {number} rate
 * @property {any} [invitation]
 * @property {any} [negotiation]
 * @property {any} [contract]
 */

/** @typedef {Object} PipelineActivityEntry
 * @property {string} id
 * @property {string} atISO
 * @property {string} text
 */

function mapApiActivityToUi(activity = []) {
  const list = Array.isArray(activity) ? activity : [];
  return list
    .map((a, idx) => {
      const at = a?.at ? new Date(a.at) : null;
      const atISO = at && Number.isFinite(at.getTime()) ? at.toISOString() : new Date().toISOString();
      const text = String(a?.text || "").trim();
      const type = String(a?.type || "").trim();
      return {
        id: `srv_${atISO}_${type || "activity"}_${idx}`,
        atISO,
        text,
      };
    })
    .filter((a) => a.text);
}

const initialState = {
  cards: [],
  selectedCampaignId: "",
  selectedCardId: null,
  /** @type {Record<string, PipelineActivityEntry[]>} */
  activityByCardId: {},
  loading: false,
};

function boardReducer(state, action) {
  switch (action.type) {
    case "SET_CARDS":
      return {
        ...state,
        cards: action.payload?.cards ?? [],
        activityByCardId: action.payload?.activityByCardId ?? {},
      };
    case "SET_LOADING":
      return { ...state, loading: Boolean(action.payload) };
    case "SET_CAMPAIGN":
      return { ...state, selectedCampaignId: action.payload };
    case "OPEN_CARD":
      return { ...state, selectedCardId: action.payload };
    case "CLOSE_CARD":
      return { ...state, selectedCardId: null };
    case "MOVE_CARD": {
      const { cardId, toStageId } = action.payload;
      return {
        ...state,
        cards: state.cards.map((c) =>
          c.id === cardId ? { ...c, stageId: toStageId } : c
        ),
      };
    }
    case "ADVANCE_CARD": {
      const { cardId } = action.payload;
      return {
        ...state,
        cards: state.cards.map((c) => {
          if (c.id !== cardId) return c;
          const next = getNextStageId(c.stageId);
          return next ? { ...c, stageId: next } : c;
        }),
      };
    }
    case "SET_ACTIVITY": {
      const { cardId, activities } = action.payload || {};
      if (!cardId) return state;
      return {
        ...state,
        activityByCardId: {
          ...state.activityByCardId,
          [cardId]: Array.isArray(activities) ? activities : [],
        },
      };
    }
    default:
      return state;
  }
}

function mapApiItemToCard(item) {
  const snapshot = item?.snapshot || {};
  const stageId = String(item?.stageId || "shortlisted");
  const brandSigned = Boolean(item?.contract?.brandSignedAt);
  const influencerSigned = Boolean(item?.contract?.influencerSignedAt);
  const isContractFinalized = stageId === "confirmed" || (brandSigned && influencerSigned);
  const contractAgreedRate = Number(item?.contract?.agreedRateInr);
  const snapshotRate = Number(snapshot.rate || 0);
  const resolvedRate =
    isContractFinalized && Number.isFinite(contractAgreedRate)
      ? contractAgreedRate
      : snapshotRate;
  const selectedPackagePlatform = String(snapshot?.selectedPackage?.platform || "").trim();
  const normalizedPlatforms = Array.isArray(snapshot.platforms) ? snapshot.platforms : [];
  const platforms = selectedPackagePlatform
    ? [selectedPackagePlatform]
    : normalizedPlatforms;
  const name =
    String(snapshot.name || "").trim() ||
    String(item?.username || "").trim() ||
    "Influencer";
  const handle = snapshot.username
    ? `@${String(snapshot.username).replace(/^@/, "")}`
    : item?.username
      ? `@${String(item.username).replace(/^@/, "")}`
      : "@unknown";

  return {
    id: String(item?._id || item?.id),
    campaignId: String(item?.campaignId),
    stageId,
    name,
    handle,
    niches: Array.isArray(snapshot.niches) ? snapshot.niches : [],
    platforms,
    followers: Number(snapshot.followers || 0),
    engagementRate: Number(snapshot.engagementRate || 0),
    rate: resolvedRate,
    invitation: item?.invitation || null,
    negotiation: item?.negotiation || null,
    contract: item?.contract || null,
    __pipelineItemId: String(item?._id || item?.id),
  };
}

export function usePipelineBoard() {
  const [state, dispatch] = useReducer(boardReducer, initialState);

  const loadCampaign = useCallback(async (campaignId) => {
    const cleanId = String(campaignId || "").trim();
    if (!cleanId) {
      dispatch({ type: "SET_CARDS", payload: [] });
      return;
    }
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      const items = await pipelineService.listItems({ campaignId: cleanId });
      const cards = items.map(mapApiItemToCard);
      const activityByCardId = {};
      for (const item of items) {
        const id = String(item?._id || item?.id || "").trim();
        if (!id) continue;
        activityByCardId[id] = mapApiActivityToUi(item?.activity);
      }
      dispatch({ type: "SET_CARDS", payload: { cards, activityByCardId } });
    } catch (e) {
      dispatch({ type: "SET_CARDS", payload: { cards: [], activityByCardId: {} } });
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, []);

  useEffect(() => {
    loadCampaign(state.selectedCampaignId);
  }, [loadCampaign, state.selectedCampaignId]);

  const moveCard = useCallback(
    (cardId, toStageId) => {
      const cardBefore = state.cards.find((c) => c.id === cardId);
      const fromStageId = cardBefore?.stageId;
      dispatch({ type: "MOVE_CARD", payload: { cardId, toStageId } });
      const card = state.cards.find((c) => c.id === cardId);
      const itemId = card?.__pipelineItemId;
      if (itemId && state.selectedCampaignId) {
        pipelineService
          .moveStage({ itemId, stageId: toStageId })
          .then((updated) => {
            dispatch({
              type: "SET_ACTIVITY",
              payload: { cardId, activities: mapApiActivityToUi(updated?.activity) },
            });
          })
          .catch((err) => {
            if (fromStageId) {
              dispatch({ type: "MOVE_CARD", payload: { cardId, toStageId: fromStageId } });
            }
            const message =
              err?.response?.data?.message ||
              err?.message ||
              "Failed to move pipeline stage";
            toast.error(message);
          });
      }
    },
    [state.cards, state.selectedCampaignId]
  );

  const advanceCard = useCallback((cardId) => {
    dispatch({ type: "ADVANCE_CARD", payload: { cardId } });
  }, []);

  const setSelectedCampaignId = useCallback((id) => {
    dispatch({ type: "SET_CAMPAIGN", payload: id });
  }, []);

  const setCards = useCallback((cards) => {
    dispatch({ type: "SET_CARDS", payload: cards });
  }, []);

  const reloadSelectedCampaign = useCallback(async () => {
    await loadCampaign(state.selectedCampaignId);
  }, [loadCampaign, state.selectedCampaignId]);

  const openCard = useCallback((cardId) => {
    dispatch({ type: "OPEN_CARD", payload: cardId });
  }, []);

  const closeCard = useCallback(() => {
    dispatch({ type: "CLOSE_CARD" });
  }, []);

  const refreshActivity = useCallback(async (cardId) => {
    const clean = String(cardId || "").trim();
    if (!clean) return;
    try {
      const raw = await pipelineService.getActivity({ itemId: clean });
      dispatch({ type: "SET_ACTIVITY", payload: { cardId: clean, activities: mapApiActivityToUi(raw) } });
    } catch {
      // non-blocking: activity is auxiliary UI
    }
  }, []);

  const addNote = useCallback(async (cardId, text) => {
    const clean = String(cardId || "").trim();
    const trimmed = String(text ?? "").trim();
    if (!clean || !trimmed) return;
    try {
      const raw = await pipelineService.addNote({ itemId: clean, text: trimmed });
      dispatch({ type: "SET_ACTIVITY", payload: { cardId: clean, activities: mapApiActivityToUi(raw) } });
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || "Failed to save note";
      toast.error(message);
    }
  }, []);

  return {
    cards: state.cards,
    selectedCampaignId: state.selectedCampaignId,
    stages: PIPELINE_STAGES,
    moveCard,
    advanceCard,
    setSelectedCampaignId,
    setCards,
    reloadSelectedCampaign,
    dragType: DT_CARD,
    selectedCardId: state.selectedCardId,
    openCard,
    closeCard,
    activityByCardId: state.activityByCardId,
    refreshActivity,
    addNote,
    loading: state.loading,
  };
}

/**
 * @param {PipelineCardModel[]} cards
 * @param {string} campaignId
 * @param {string} nicheFilter — "All" or niche label
 */
export function useFilteredPipelineCards(cards, campaignId, nicheFilter) {
  return useMemo(() => {
    let list = cards.filter((c) => c.campaignId === campaignId);
    if (nicheFilter && nicheFilter !== "All") {
      list = list.filter((c) => c.niches.includes(nicheFilter));
    }
    return list;
  }, [cards, campaignId, nicheFilter]);
}
